import type { StorageKeys } from './storage';
import { getItem, removeItem, setItem } from './storage';

export type GlobalTokens = {
  accessToken: string;
  refreshToken: string;
  sessionId: string;
};

export type TenantSession = {
  tenantId: number;
  tenantName: string;
  tenantLogo: string | null;
  userId: number;
  email: string;
  region: string;
  isCoAlert: boolean;
  accessToken: string;
  refreshToken: string;
  sessionId: string;
  hasActiveAlarm?: boolean;
  hasUnreadMessages?: boolean;
  hasActiveImOk?: boolean;
};

export function loadGlobalTokens(): GlobalTokens | null {
  const accessToken = getItem('coreApiAccessToken') as string | null;
  const refreshToken = getItem('coreApiRefreshToken') as string | null;
  const sessionId = getItem('coreApiSessionId') as string | null;
  if (!accessToken || !refreshToken || !sessionId) return null;
  return { accessToken, refreshToken, sessionId };
}

export function saveGlobalTokens({ accessToken, refreshToken, sessionId }: GlobalTokens): void {
  setItem('coreApiAccessToken', accessToken);
  setItem('coreApiRefreshToken', refreshToken);
  setItem('coreApiSessionId', sessionId);
}

export function clearGlobalTokens(): void {
  removeItem('coreApiAccessToken');
  removeItem('coreApiRefreshToken');
  removeItem('coreApiSessionId');
}

const TENANT_SESSIONS_KEY: StorageKeys = 'tenantSessions';
const ACTIVE_TENANT_ID_KEY: StorageKeys = 'activeTenantId';

export function loadTenantSessionsFromStorage(): TenantSession[] {
  const raw = getItem(TENANT_SESSIONS_KEY);
  if (!raw) {
    return [];
  } else if (Array.isArray(raw)) {
    return raw as TenantSession[];
  } else if (typeof raw === 'object') {
    // Migration: convert old Record<string, TenantSession> format to array.
    return Object.values(raw) as TenantSession[];
  } else {
    return [];
  }
}

export function saveTenantSessionsToStorage(sessions: TenantSession[]): void {
  setItem(TENANT_SESSIONS_KEY, sessions);
}

export function loadActiveTenantIdFromStorage(): number | null {
  const raw = getItem(ACTIVE_TENANT_ID_KEY);
  if (raw == null) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function saveActiveTenantIdToStorage(tenantId: number | null): void {
  if (tenantId == null) {
    removeItem(ACTIVE_TENANT_ID_KEY);
    return;
  }
  // Storage helper only accepts string/boolean/object values – coerce the id
  // to its string form and parse it back on reload (see `loadActiveTenantIdFromStorage`).
  setItem(ACTIVE_TENANT_ID_KEY, String(tenantId));
}

export function clearAllTenantStorage(): void {
  removeItem(TENANT_SESSIONS_KEY);
  removeItem(ACTIVE_TENANT_ID_KEY);
}
